import React, { useRef, useState, useEffect } from "react";

type Tool = "pen" | "eraser";

interface DrawingPadProps {
  drawingPadData?: string | null;
  setDrawingPadData: React.Dispatch<React.SetStateAction<string | null>>;
  setCanvasWidth: React.Dispatch<React.SetStateAction<Number>>;
  setDrawingChange: React.Dispatch<React.SetStateAction<boolean>>;
  ratio?: number;
  width?: number;
  height?: number;
  backgroundColor?: string;
  defaultColor?: string;
  defaultLineWidth?: number;
  showToolbar?: boolean;
}

const DrawingPad: React.FC<DrawingPadProps> = ({
  drawingPadData,
  setDrawingPadData,
  setCanvasWidth,
  setDrawingChange,
  ratio,
  width: fixedWidth,
  height: fixedHeight,
  backgroundColor = "white",
  defaultColor = "#000000",
  defaultLineWidth = 2,
  showToolbar = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [tool, setTool] = useState<Tool>("pen");
  const [color, setColor] = useState<string>(defaultColor);
  const [lineWidth, setLineWidth] = useState<number>(defaultLineWidth);
  const [history, setHistory] = useState<string[]>([]);
  const [historyStep, setHistoryStep] = useState<number>(-1);
  const [canvasSize, setCanvasSize] = useState({ width: fixedWidth || 600, height: fixedHeight || 600 });
  const [isInitialized, setIsInitialized] = useState(false);

  // Handle responsive sizing based on ratio
  useEffect(() => {
    if (!ratio || ratio <= 0) {
      setCanvasSize({ width: fixedWidth || 600, height: fixedHeight || 600 });
      return;
    }

    const updateSize = (width: number) => {
      if (width > 0) {
        const calculatedHeight = width * (ratio / 100);
        setCanvasSize({ width, height: calculatedHeight });
        setCanvasWidth(width);
      }
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updateSize(entry.contentRect.width);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [ratio, fixedWidth, fixedHeight]);

  // Initialize canvas - load existing data or create blank canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Load existing data if provided
    if (drawingPadData) {
      const img = new Image();
      img.src = drawingPadData;
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        if (!isInitialized) {
          saveToHistory();   // only for first load
          setIsInitialized(true);
        }
      };
      img.onerror = () => {
        console.error("Failed to load drawingPadData image");
      };
      return;
    }

    // If no base64 provided → blank canvas
    if (!isInitialized) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      saveToHistory();
      setIsInitialized(true);
    }
  }, [backgroundColor, canvasSize, drawingPadData]);

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL();
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyStep + 1);
      return [...newHistory, dataUrl];
    });
    setHistoryStep((prev) => prev + 1);
    
    // Update parent component with new data
    setDrawingPadData(dataUrl);
  };

  const undo = () => {
    if (historyStep > 0) {
      setHistoryStep((prev) => prev - 1);
      restoreFromHistory(historyStep - 1);
    }
  };

  const redo = () => {
    if (historyStep < history.length - 1) {
      setHistoryStep((prev) => prev + 1);
      restoreFromHistory(historyStep + 1);
    }
  };

  const restoreFromHistory = (step: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;
    const img = new Image();
    img.src = history[step];
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      // Update parent component when restoring from history
      setDrawingPadData(history[step]);
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    setDrawing(true);
    setDrawingChange(true);
    draw(e);
  };

  const stopDrawing = () => {
    if (drawing) {
      setDrawing(false);
      const ctx = canvasRef.current?.getContext("2d");
      ctx?.beginPath();
      saveToHistory();
    }
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    let x: number, y: number;
    if ("touches" in e) {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      x = (e.touches[0].clientX - rect.left) * scaleX;
      y = (e.touches[0].clientY - rect.top) * scaleY;
    } else {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      x = (e.nativeEvent.clientX - rect.left) * scaleX;
      y = (e.nativeEvent.clientY - rect.top) * scaleY;
    }

    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = tool === "eraser" ? 20 : lineWidth;
    ctx.strokeStyle = tool === "eraser" ? backgroundColor : color;

    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    saveToHistory();
  };

  const saveImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = "drawing.png";
    link.click();
  };

  const containerStyle = ratio && ratio > 0 ? { width: '100%' } : { display: 'inline-block' };

  return (
    <div ref={containerRef} style={containerStyle}>
      {showToolbar && (
        <div className="draw-tool">
          <button
            type="button"
            onClick={() => setTool("pen")}
            title="Pen"
            className={`draw-button icon-pen ${
              tool === "pen" ? "draw-button icon-pen draw-selected" : "draw-button icon-pen"
            }`}
          >
            &nbsp;
          </button>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={tool === "eraser"}
            className="draw-color"
          />
          <input
            type="range"
            min="1"
            max="10"
            value={lineWidth}
            onChange={(e) => setLineWidth(Number(e.target.value))}
            className="draw-thickness"
            title={`Line width: ${lineWidth}`}
          />
          <button
            type="button"
            onClick={undo}
            disabled={historyStep <= 0}
            className="draw-button icon-undo"
            title="Undo"
          >
            &nbsp;
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={historyStep >= history.length - 1}
            className="draw-button icon-redo"
            title="Redo"
          >
            &nbsp;
          </button>
          <button
            type="button"
            onClick={() => setTool("eraser")}
            title="Eraser"
            className={`draw-button icon-eraser ${
              tool === "eraser" ? "draw-button icon-eraser draw-selected" : "draw-button icon-eraser"
            }`}
          >
            &nbsp;
          </button>
          
          <button
            type="button"
            onClick={saveImage}
            className="draw-button icon-download fright"
            title="Download"
          >
            &nbsp;
          </button>
          <button
            type="button"
            onClick={clearCanvas}
            className="draw-button icon-trash fright"
            title="Clear"
          >
            &nbsp;
          </button>
        </div>
      )}
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        style={{ 
          border: "1px solid #ddd", 
          touchAction: "none", 
          display: "block",
          marginBottom: "20px",
          width: '100%',
          height: 'auto'
        }}
        onMouseDown={startDrawing}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onMouseMove={draw}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
    </div>
  );
};

export default DrawingPad;